/**
 * Bekleyen Devamsız Vardiyalar — BOSS Genel Bakış feed kartı.
 *
 * `?devamsiz=VID` URL paramı geldiğinde o satır kırmızı yanıp söner
 * (görev modülündeki vurgu paterninin aynısı: sessionStorage'da tutulur,
 * sayfa değişikliklerinde devam eder, "Karar Ver" tıklayınca durur).
 */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, ChevronRight } from 'lucide-react'
import {
  bekleyenDevamsizVardiyalar,
  type Vardiya,
} from '@/lib/services/vardiya-service'
import { DevamsizKararModal } from './DevamsizKararModal'
import type { KullaniciPersonel, Sube } from '@/types'

const VURGU_KEY = 'devamsizVurguId'
const VURGU_STIL = `
@keyframes devamsizYanip {
  0%, 100% { background-color: rgb(254 226 226); border-color: rgb(252 165 165); }
  50% { background-color: rgb(254 202 202); border-color: rgb(248 113 113); }
}
.devamsiz-vurgu {
  animation: devamsizYanip 1.2s ease-in-out infinite;
}
`

interface Props {
  personeller: KullaniciPersonel[]
  subeler: Sube[]
  onYenile: () => void
}

export function BekleyenDevamsizKart({ personeller, subeler, onYenile }: Props) {
  const [vardiyalar, setVardiyalar] = useState<Vardiya[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [secili, setSecili] = useState<Vardiya | null>(null)
  const [vurguId, setVurguId] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // URL'den ?devamsiz=ID al, sessionStorage'a kaydet, URL'i temizle
  useEffect(() => {
    const urlId = searchParams.get('devamsiz')
    if (urlId) {
      sessionStorage.setItem(VURGU_KEY, urlId)
      const yeni = new URLSearchParams(searchParams)
      yeni.delete('devamsiz')
      setSearchParams(yeni, { replace: true })
    }
    const kayitli = sessionStorage.getItem(VURGU_KEY)
    if (kayitli) setVurguId(kayitli)
  }, [searchParams, setSearchParams])

  // Veriyi yükle
  useEffect(() => {
    let aktif = true
    setYukleniyor(true)
    bekleyenDevamsizVardiyalar()
      .then((d) => {
        if (aktif) setVardiyalar(d)
      })
      .catch(() => {
        if (aktif) setVardiyalar([])
      })
      .finally(() => {
        if (aktif) setYukleniyor(false)
      })
    return () => {
      aktif = false
    }
  }, [])

  // Vurgulanan satıra scroll
  useEffect(() => {
    if (!vurguId) return
    const el = document.getElementById(`devamsiz-${vurguId}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [vurguId, vardiyalar])

  const personelMap = useMemo(() => {
    const m = new Map<string, KullaniciPersonel>()
    personeller.forEach((p) => m.set(p.id, p))
    return m
  }, [personeller])

  const subeMap = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  function vurguyuDurdur() {
    sessionStorage.removeItem(VURGU_KEY)
    setVurguId(null)
  }

  async function kararKaydedildi() {
    vurguyuDurdur()
    // Listeyi yeniden çek + parent'ı bilgilendir
    const yeni = await bekleyenDevamsizVardiyalar()
    setVardiyalar(yeni)
    onYenile()
  }

  if (yukleniyor) return null
  if (vardiyalar.length === 0) return null

  return (
    <>
      <style>{VURGU_STIL}</style>
      <div className="card border-2 border-red-200 bg-red-50/50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Karar Bekleyen Devamsızlıklar ({vardiyalar.length})
          </h2>
        </div>

        <div className="space-y-2">
          {vardiyalar.map((v) => {
            const personel = personelMap.get(v.personelId)
            const sube = subeMap.get(v.subeId)
            const isVurgu = vurguId === v.id
            const tarihEt = v.tarih.split('-').reverse().join('.')
            return (
              <div
                id={`devamsiz-${v.id}`}
                key={v.id}
                className={`bg-white rounded-xl border-2 p-3 transition-colors ${
                  isVurgu ? 'devamsiz-vurgu' : 'border-red-100'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {personel?.isim ?? 'Bilinmeyen personel'}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {tarihEt} · {v.baslangic}–{v.bitis}
                      {sube && <span> · {sube.isim}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      vurguyuDurdur()
                      setSecili(v)
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex-shrink-0"
                  >
                    Karar Ver
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {secili && (
        <DevamsizKararModal
          vardiya={secili}
          personel={personelMap.get(secili.personelId)}
          acik={true}
          onKapat={() => setSecili(null)}
          onKaydedildi={kararKaydedildi}
        />
      )}
    </>
  )
}
